import assign from "lodash.assign";
import isEmpty from "lodash.isempty";
import Validation from "common/utils/Validation";
import { setErrorInErrors, clearErrorFromErrors } from "../utils/errorsUtil";
import { ADDRESS_FIELDS, SUBSCRIPTION_FORM_BILLING_ADDRESS_FIELDS } from "common/constants/Constants";
import REDUX_ERROR_TYPES from "constants/ReduxErrorTypes";

export const BILLING_ADDRESS_FORM_UPDATE_COUNTRY = "BILLING_ADDRESS_FORM_UPDATE_COUNTRY";
export const BILLING_ADDRESS_FORM_UPDATE_STATE = "BILLING_ADDRESS_FORM_UPDATE_STATE";
export const BILLING_ADDRESS_FORM_UPDATE_POSTALCODE = "BILLING_ADDRESS_FORM_UPDATE_POSTALCODE";
export const BILLING_ADDRESS_FORM_UPDATE_FIRST_NAME = "BILLING_ADDRESS_FORM_UPDATE_FIRST_NAME";
export const BILLING_ADDRESS_FORM_UPDATE_LAST_NAME = "BILLING_ADDRESS_FORM_UPDATE_LAST_NAME";
export const BILLING_ADDRESS_FORM_UPDATE_LINE_1 = "BILLING_ADDRESS_FORM_UPDATE_LINE_1";
export const BILLING_ADDRESS_FORM_UPDATE_LINE_2 = "BILLING_ADDRESS_FORM_UPDATE_LINE_2";
export const BILLING_ADDRESS_FORM_UPDATE_CITY = "BILLING_ADDRESS_FORM_UPDATE_CITY";
export const BILLING_ADDRESS_FORM_SHOW_ERROR = "BILLING_ADDRESS_FORM_SHOW_ERROR";
export const BILLING_ADDRESS_FORM_CLEAR_ERROR = "BILLING_ADDRESS_FORM_CLEAR_ERROR";
export const SUBSCRIPTION_FORM_CLEAR_ERROR = "SUBSCRIPTION_FORM_CLEAR_ERROR";

const defaultState = {
    errors: {}
};

export function billingAddressFormReducer(state = defaultState, action) {
    switch (action.type) {
        case BILLING_ADDRESS_FORM_UPDATE_COUNTRY:
            return assign({}, state, {
                country: action.country
            });
        case BILLING_ADDRESS_FORM_UPDATE_STATE:
            return assign({}, state, {
                state: action.state
            });
        case BILLING_ADDRESS_FORM_UPDATE_POSTALCODE:
            return assign({}, state, {
                postalCode: action.postalCode
            });
        case BILLING_ADDRESS_FORM_UPDATE_FIRST_NAME:
            return assign({}, state, {
                firstName: action.firstName
            });
        case BILLING_ADDRESS_FORM_UPDATE_LAST_NAME:
            return assign({}, state, {
                lastName: action.lastName
            });
        case BILLING_ADDRESS_FORM_UPDATE_LINE_1:
            return assign({}, state, {
                addressLine1: action.addressLine1
            });
        case BILLING_ADDRESS_FORM_UPDATE_LINE_2:
            return assign({}, state, {
                addressLine2: action.addressLine2
            });
        case BILLING_ADDRESS_FORM_UPDATE_CITY:
            return assign({}, state, {
                city: action.city
            });
        case BILLING_ADDRESS_FORM_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case BILLING_ADDRESS_FORM_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        default:
            return state;
    }
}

export function setName() {
    return (dispatch, getState) => {
        const {
            bulkSubscriptionForm: {
                firstName,
                lastName
            }
        } = getState();

        dispatch(updateFirstName(firstName));
        dispatch(updateLastName(lastName));
    };
}

export function updateError(error, type) {
    return (dispatch) => {
        if (!error) {
            dispatch(clearError(type));

            return;
        }

        dispatch(showError({
            type,
            message: error.message
        }));
    };
}

export function validateBillingAddressForSubscription() {
    return (dispatch, getState) => {
        const {
            subscriptionForm: {
                selectedProductDetails: {
                    productType
                }
            }
        } = getState();

        const fields = SUBSCRIPTION_FORM_BILLING_ADDRESS_FIELDS[productType];

        if (!fields) {
            return false;
        }

        return dispatch(validateBillingAddress(fields));
    };
}

export function validateBillingAddress(fields = []) {
    return (dispatch, getState) => {
        const billingAddressForm = getState().billingAddressForm;

        fields.forEach((field) => dispatch(validateAndUpdate(field, billingAddressForm[field])));

        const { errors } = getState().billingAddressForm;

        return isEmpty(errors);
    };
}

export function validateAndUpdate(field, value) {
    return (dispatch) => {
        switch (field) {
            case ADDRESS_FIELDS.COUNTRY:
                return dispatch(validateAndUpdateCountry(value));
            case ADDRESS_FIELDS.STATE:
                return dispatch(validateAndUpdateState(value));
            case ADDRESS_FIELDS.POSTAL_CODE:
                return dispatch(validateAndUpdatePostalCode(value));
            case ADDRESS_FIELDS.CITY:
                return dispatch(validateAndUpdateCity(value));
            case ADDRESS_FIELDS.ADDRESS_LINE_1:
                return dispatch(validateAndUpdateAddressLine1(value));
            case ADDRESS_FIELDS.FIRST_NAME:
                return dispatch(validateAndUpdateFirstName(value));
            case ADDRESS_FIELDS.LAST_NAME:
                return dispatch(validateAndUpdateLastName(value));
        }
    };
}

export function validateAndUpdateCountry(country) {
    return (dispatch) => {
        const error = Validation.validateCountry(country);

        dispatch(updateCountry(country));
        dispatch(updateError(error, "country"));
    };
}

export function validateAndUpdateState(state) {
    return (dispatch, getState) => {
        const country = getState().billingAddressForm.country;
        const error = Validation.validateStateOrProvince(country, state);

        dispatch(clearSubscriptionFormError(REDUX_ERROR_TYPES.CANCEL_NOTICE_UNCHECKED));
        dispatch(updateState(state));
        dispatch(updateError(error, "state"));
    };
}

export function validateAndUpdatePostalCode(postalCode) {
    return (dispatch, getState) => {
        const country = getState().billingAddressForm.country;
        const error = Validation.validatePostalCode(country, postalCode);

        dispatch(updatePostalCode(postalCode));
        dispatch(updateError(error, "postalCode"));
    };
}

export function validateAndUpdateAddressLine1(addressLine1) {
    return (dispatch) => {
        const error = Validation.validateFieldIsRequired(addressLine1)
            || Validation.validateEnglishCharacters(addressLine1);

        dispatch(updateAddressLine1(addressLine1));
        dispatch(updateError(error, "addressLine1"));
    };
}

export function validateAndUpdateAddressLine2(addressLine2) {
    return (dispatch) => {
        const error = Validation.validateEnglishCharacters(addressLine2);

        dispatch(updateAddressLine2(addressLine2));
        dispatch(updateError(error, "addressLine2"));
    };
}

export function validateAndUpdateCity(city) {
    return (dispatch) => {
        const error = Validation.validateFieldIsRequired(city)
            || Validation.validateEnglishCharacters(city);

        dispatch(updateCity(city));
        dispatch(updateError(error, "city"));
    };
}

export function validateAndUpdateFirstName(firstName) {
    return (dispatch) => {
        const error = Validation.validateFieldIsRequired(firstName)
            || Validation.validateEnglishCharacters(firstName);

        dispatch(updateFirstName(firstName));
        dispatch(updateError(error, "firstName"));
    };
}

export function validateAndUpdateLastName(lastName) {
    return (dispatch) => {
        const error = Validation.validateFieldIsRequired(lastName)
            || Validation.validateEnglishCharacters(lastName);

        dispatch(updateLastName(lastName));
        dispatch(updateError(error, "lastName"));
    };
}

export function updateFirstName(firstName) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_FIRST_NAME,
        firstName
    };
}

export function updateLastName(lastName) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_LAST_NAME,
        lastName
    };
}

export function updateAddressLine2(addressLine2) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_LINE_2,
        addressLine2
    };
}

export function updateAddressLine1(addressLine1) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_LINE_1,
        addressLine1
    };
}

export function updateCountry(country) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_COUNTRY,
        country
    };
}

export function updateState(state) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_STATE,
        state
    };
}

export function updatePostalCode(postalCode) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_POSTALCODE,
        postalCode
    };
}

export function updateCity(city) {
    return {
        type: BILLING_ADDRESS_FORM_UPDATE_CITY,
        city
    };
}

export function showError(error) {
    return {
        type: BILLING_ADDRESS_FORM_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: BILLING_ADDRESS_FORM_CLEAR_ERROR,
        errorType
    };
}

export function clearSubscriptionFormError(errorType) {
    return {
        type: SUBSCRIPTION_FORM_CLEAR_ERROR,
        errorType
    };
}

export default {
    updateCountry,
    updateState,
    updatePostalCode,
    updateFirstName,
    updateLastName,
    updateAddressLine1,
    updateAddressLine2,
    updateCity,
    updateError,
    setName,
    showError,
    clearError,
    validateBillingAddress,
    validateBillingAddressForSubscription,
    validateAndUpdateCountry,
    validateAndUpdateState,
    validateAndUpdateCity,
    validateAndUpdatePostalCode,
    validateAndUpdateAddressLine1,
    validateAndUpdateAddressLine2,
    validateAndUpdateFirstName,
    validateAndUpdateLastName
};
